import React from 'react';
import SectionCTA from './SectionCTA';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Flag01Icon,
  Compass01Icon,
  CourseIcon,
  GolfBatIcon,
  CompassIcon,
  GolfHoleIcon,
  Location01Icon,
  Location02Icon,
  Location03Icon,
  Location04Icon,
  Target01Icon,
  Target02Icon,
  Target03Icon,
  GolfBallIcon,
  GolfCartIcon,
  Award02Icon,
  ChartEvaluationIcon,
  Flag02Icon
} from '@hugeicons/core-free-icons';

const ChapterCards = () => {
  // Icon map to convert string icon names to React components
  const iconMap = {
    'hgi-flag-01': Flag01Icon,
    'hgi-compass-01': Compass01Icon,
    'hgi-course': CourseIcon,
    'hgi-golf-bat': GolfBatIcon,
    'hgi-compass': CompassIcon,
    'hgi-golf-hole': GolfHoleIcon,
    'hgi-location-01': Location01Icon,
    'hgi-location-02': Location02Icon,
    'hgi-location-03': Location03Icon,
    'hgi-location-04': Location04Icon,
    'hgi-target-01': Target01Icon,
    'hgi-target-02': Target02Icon,
    'hgi-target-03': Target03Icon,
    'hgi-golf-ball': GolfBallIcon,
    'hgi-golf-cart': GolfCartIcon,
    'hgi-award-02': Award02Icon,
    'hgi-chart-evaluation': ChartEvaluationIcon,
    'hgi-flag-02': Flag02Icon
  };

  const chapters = [
    {
      id: 1,
      title: "Chapter 1: How to Hit More Greens",
      description: "Learn the 3 critical numbers every caddie knows before pulling a club. Discover why aiming at the flag is often the wrong strategy.",
      icon: "hgi-flag-01",
      iconBg: "from-green-600 to-green-800",
      bgClass: "chapter-1-bg",
      features: [
        { icon: "hgi-location-02", text: "Distance to front, back, and pin" },
        { icon: "hgi-target-01", text: "Strategic green targeting techniques" },
        { icon: "hgi-golf-hole", text: "Pin placement reading mastery" }
      ]
    },
    {
      id: 2,
      title: "Chapter 2: Aim Closer",
      description: "The intermediate target method that tour pros use for perfect alignment. Stop missing targets by 20-30 yards.",
      icon: "hgi-compass-01",
      iconBg: "from-amber-700 to-amber-900",
      bgClass: "chapter-2-bg",
      features: [
        { icon: "hgi-golf-ball", text: "Pick targets 2-3 feet in front of ball" },
        { icon: "hgi-location-03", text: "Eliminate alignment guesswork" },
        { icon: "hgi-target-02", text: "Tour pro setup sequence" }
      ]
    },
    {
      id: 3,
      title: "Chapter 3: Routine",
      description: "Build an unshakeable pre-shot routine that eliminates doubt and performs under pressure.",
      icon: "hgi-course",
      iconBg: "from-orange-500 to-orange-700",
      bgClass: "chapter-3-bg",
      features: [
        { icon: "hgi-target-02", text: "5-step framework for consistency" },
        { icon: "hgi-golf-cart", text: "Mental game safeguards" },
        { icon: "hgi-award-02", text: "Pressure-proof your process" }
      ]
    },
    {
      id: 4,
      title: "Chapter 4: Know Your Numbers",
      description: "Discover your true stock yardages and stop guessing distances. Eliminate under-clubbing forever.",
      icon: "hgi-golf-bat",
      iconBg: "from-green-600 to-green-800",
      bgClass: "chapter-4-bg",
      features: [
        { icon: "hgi-location-04", text: "Calculate real carry distances" },
        { icon: "hgi-target-03", text: "Stop using \"best case\" numbers" },
        { icon: "hgi-chart-evaluation", text: "Build confidence with facts" }
      ]
    },
    {
      id: 5,
      title: "Chapter 5: Read Every Putt",
      description: "The \"Compass Method\" for reading putts from 4 angles. See breaks that 90% of golfers miss.",
      icon: "hgi-compass",
      iconBg: "from-purple-500 to-purple-700",
      bgClass: "chapter-5-bg",
      features: [
        { icon: "hgi-location-01", text: "Read from 4 different angles" },
        { icon: "hgi-golf-ball", text: "Identify subtle breaks" },
        { icon: "hgi-target-03", text: "Pro-level green reading" }
      ]
    },
    {
      id: 6,
      title: "Chapter 6: Chip More, Pitch Less",
      description: "The 80/20 short game rule that tour pros follow. Stop reaching for the lob wedge.",
      icon: "hgi-golf-hole",
      iconBg: "from-orange-500 to-orange-700",
      bgClass: "chapter-6-bg",
      features: [
        { icon: "hgi-golf-bat", text: "When to chip vs pitch" },
        { icon: "hgi-flag-02", text: "Club selection mastery" },
        { icon: "hgi-target-01", text: "Consistent contact drills" }
      ]
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Watercolor Golf Course Background with Parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://res.cloudinary.com/dzcrxivpm/image/upload/v1758893558/openart-an-imaginative-watercolor-style-image-of-a-pristine-golf-course-on-the-perfect-day-of-the-year_ljdOIOCo_upscaled_pcemjl.webp"
          alt="Watercolor golf course background"
          width="1920"
          height="1080"
          className="parallax-bg"
          data-speed="0.2"
          loading="lazy"
        />
      </div>

      {/* Light gradient overlay to ensure readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/75 via-white/85 to-white/90"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="text-green-800 font-bold text-sm uppercase tracking-wider bg-green-50 px-4 py-2 rounded-full">What's Inside</span>
          </div>
          <h2 className="font-playfair font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-brand-brown mb-8 leading-tight">
            What You'll Discover in<br/>
            <span className="text-brand-green font-bold">Golf Like a Caddie: Volume 1</span>
          </h2>
          <p className="text-xl text-gray-800 max-w-4xl mx-auto leading-relaxed">
            A collection of timeless lessons — caddie wisdom passed down through experience,
            tested under pressure, and grounded in the reality of how the game is actually played.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
          {chapters.slice(0, 4).map((chapter) => {
            const ChapterIcon = iconMap[chapter.icon];
            return (
              <div key={chapter.id} className={`chapter-card ${chapter.bgClass} group`}>
                <div className="flex items-start space-x-6">
                  <div className={`chapter-card-icon bg-gradient-to-br ${chapter.iconBg} p-5 rounded-2xl flex-shrink-0 shadow-lg`}>
                    <HugeiconsIcon icon={ChapterIcon} size={48} color="white" />
                  </div>
                  <div>
                    <h3 className="font-inter font-bold text-2xl text-brand-brown mb-4">{chapter.title}</h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {chapter.description.split('**').map((part, index) =>
                        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                      )}
                    </p>
                    <ul className="text-sm text-gray-600 space-y-2">
                      {chapter.features.map((feature, index) => {
                        const FeatureIcon = iconMap[feature.icon];
                        return (
                          <li key={index} className="flex items-center">
                            <HugeiconsIcon icon={FeatureIcon} size={16} className="text-brand-green mr-2" />
                            {feature.text}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bonus Chapters Preview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {chapters.slice(4, 6).map((chapter) => {
            const ChapterIcon = iconMap[chapter.icon];
            return (
              <div key={chapter.id} className={`chapter-card ${chapter.bgClass} group`}>
                <div className="flex items-start space-x-6">
                  <div className={`chapter-card-icon bg-gradient-to-br ${chapter.iconBg} text-white p-5 rounded-2xl flex-shrink-0 shadow-lg`}>
                    <HugeiconsIcon icon={ChapterIcon} size={48} color="white" />
                  </div>
                  <div>
                    <h3 className="font-inter font-bold text-2xl text-brand-brown mb-4">{chapter.title}</h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {chapter.description.split('**').map((part, index) =>
                        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                      )}
                    </p>
                    <ul className="text-sm text-gray-600 space-y-2">
                      {chapter.features.map((feature, index) => {
                        const FeatureIcon = iconMap[feature.icon];
                        return (
                          <li key={index} className="flex items-center">
                            <HugeiconsIcon icon={FeatureIcon} size={16} className="text-brand-green mr-2" />
                            {feature.text}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop CTA */}
        <SectionCTA className="mt-16" />
      </div>
    </section>
  );
};

export default ChapterCards;